import React from 'react';
import { connect } from 'react-redux';
import { Subscription } from 'rxjs';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Grid,
} from '@material-ui/core';

import { RootState } from '../interfaces/States';
import ANIMATE from '../constants/Animate';
import I18n from '../i18n';
import {
  abandonEnrolmentFlow,
  getCourse,
  getEnrolmentBlockReason,
  saveEnrolmentConsent,
} from '../mocks/sesMockApi';
import { courseFromHistoryState, isEnrolmentCourseFull } from '../mocks/enrolmentCourses';
import EnrolmentCourseHeader from './EnrolmentCourseHeader';
import { isDisableSes } from '../utils/isDisableSes';
import OdooService from '../services/OdooService';
import EnrolmentBlacklistStop from './EnrolmentBlacklistStop';
import { sesFnStart, sesFnEnd, sesIoIn, sesIoOut } from '../mocks/sesFnTrace';

import '../theme/newBooking.scss';
import '../theme/studentEnrolment.scss';

type RouteParams = { courseId: string };

type Props = RouteComponentProps<RouteParams> & {
  i18n: RootState['i18n'];
  user: RootState['user'];
};

interface State {
  termsAccepted: boolean;
  scrolledToBottom: boolean;
  showTermsError: boolean;
  confirmCancel: boolean;
  /** HTML from SES settings for the current language. Null uses the i18n fallback. */
  policyHtml: string | null;
  policyReady: boolean;
}

/**
 * Booking & cancellation procedures — scroll to bottom, then Agree to continue.
 * Body is the SES settings HTML for the active language. i18n text is used when
 * that language is empty or the request fails.
 * PICS / Direct Marketing sections removed per SES comments 2026-10-23.
 */
class EnrolmentTerms extends React.Component<Props, State> {
  private policyRef: HTMLDivElement | null = null;
  private proceduresSub: Subscription | null = null;
  private proceduresReq = 0;

  constructor(props: Props) {
    super(props);
    sesFnStart('constructor');
    try {
      this.state = {
        termsAccepted: false,
        scrolledToBottom: false,
        showTermsError: false,
        confirmCancel: false,
        policyHtml: null,
        policyReady: false,
      };
    } finally {
      sesFnEnd('constructor');
    }
  }

  private courseId(): number {
    sesFnStart('courseId');
    try {
      return parseInt(this.props.match.params.courseId, 10);
    } finally {
      sesFnEnd('courseId');
    }
  }

  private bookerId(): number | undefined {
    sesFnStart('bookerId');
    try {
      const id = this.props.user?.userProfile?.partner_id;
      return id != null ? Number(id) : undefined;
    } finally {
      sesFnEnd('bookerId');
    }
  }

  private resolvedCourse() {
    const courseId = this.courseId();
    return (
      courseFromHistoryState(this.props.location.state, courseId) ||
      getCourse(courseId)
    );
  }

  private onPolicyScroll = () => {
    const el = this.policyRef;
    if (!el || this.state.scrolledToBottom) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= 8) {
      this.setState({ scrolledToBottom: true });
    }
  };

  private setPolicyRef = (el: HTMLDivElement | null) => {
    this.policyRef = el;
    if (el) {
      window.requestAnimationFrame(() => this.checkPolicyScrollComplete());
    }
  };

  componentDidMount() {
    this.loadProcedures();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.user?.token !== this.props.user?.token ||
      prevProps.i18n?.locale !== this.props.i18n?.locale
    ) {
      this.loadProcedures();
      return;
    }
    this.checkPolicyScrollComplete();
  }

  componentWillUnmount() {
    this.proceduresReq += 1;
    if (this.proceduresSub) {
      this.proceduresSub.unsubscribe();
      this.proceduresSub = null;
    }
  }

  private loadProcedures() {
    if (this.proceduresSub) {
      this.proceduresSub.unsubscribe();
      this.proceduresSub = null;
    }
    const req = ++this.proceduresReq;
    const token = this.props.user?.token;
    if (!token || isDisableSes()) {
      this.setState(
        { policyHtml: null, policyReady: true, scrolledToBottom: false },
        () => this.checkPolicyScrollComplete()
      );
      return;
    }
    this.setState({
      policyHtml: null,
      policyReady: false,
      scrolledToBottom: false,
    });
    const locale = this.props.i18n?.locale;
    this.proceduresSub = OdooService.getEnrolmentProcedures(token, locale).subscribe(
      (res) => {
        if (req !== this.proceduresReq) {
          return;
        }
        const html =
          res && res.code === 200 && typeof res.html === 'string'
            ? res.html.trim()
            : '';
        this.setState(
          {
            policyHtml: html || null,
            policyReady: true,
            scrolledToBottom: false,
            showTermsError: false,
          },
          () => this.checkPolicyScrollComplete()
        );
      },
      () => {
        if (req !== this.proceduresReq) {
          return;
        }
        this.setState(
          { policyHtml: null, policyReady: true, scrolledToBottom: false },
          () => this.checkPolicyScrollComplete()
        );
      }
    );
  }

  private checkPolicyScrollComplete() {
    const el = this.policyRef;
    if (!this.state.policyReady || !el || this.state.scrolledToBottom) {
      return;
    }
    // Not laid out yet — do not treat a zero-height box as "already at the bottom".
    if (el.clientHeight < 40) {
      return;
    }
    // Short content that does not overflow is already fully visible.
    if (el.scrollHeight <= el.clientHeight + 8) {
      this.setState({ scrolledToBottom: true });
    }
  }

  private onAgree() {
    sesFnStart('onAgree');
    try {
      sesIoIn('onAgree', '!scrolledToBottom', 'expr=!this.state.scrolledToBottom');
      if (!this.state.scrolledToBottom) {
        sesIoOut('onAgree', '!scrolledToBottom', 'branch=true');
        this.setState({ showTermsError: true });
        return;
      }
      sesIoOut('onAgree', '!scrolledToBottom', 'branch=false continue');

      this.setState({ termsAccepted: true, showTermsError: false });
      saveEnrolmentConsent(
        {
          courseId: this.courseId(),
          termsAccepted: true,
          optOutMarketing: false,
        },
        this.bookerId()
      );
      this.props.history.push({
        pathname: `/learn/enrolment/${this.courseId()}/students`,
        state: { course: this.resolvedCourse() },
      });
    } finally {
      sesFnEnd('onAgree');
    }
  }

  private onBack() {
    sesFnStart('onBack');
    try {
      const from =
        (this.props.location.state as { termsFrom?: string } | undefined)
          ?.termsFrom || 'detail';
      sesIoIn('onBack', 'from_===_list', 'expr=from === list');
      if (from === 'list') {
        sesIoOut('onBack', 'from_===_list', 'branch=true');
        this.props.history.push('/learn/enrolment');
        return;
      }
      sesIoOut('onBack', 'from_===_list', 'branch=false continue');
      this.props.history.push({
        pathname: `/learn/enrolment/${this.courseId()}`,
        state: { course: this.resolvedCourse() },
      });
    } finally {
      sesFnEnd('onBack');
    }
  }

  private onConfirmCancel() {
    sesFnStart('onConfirmCancel');
    try {
      abandonEnrolmentFlow(this.courseId(), this.bookerId());
      this.setState({ confirmCancel: false });
      this.props.history.push('/learn/enrolment');
    } finally {
      sesFnEnd('onConfirmCancel');
    }
  }

  private policyParagraphs(): string[] {
    const raw = I18n.t('ENROLMENT.TERMS.POLICY-BODY');
    if (!raw || raw === 'ENROLMENT.TERMS.POLICY-BODY') {
      return [];
    }
    return raw.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  }

  render() {
    sesFnStart('render');
    try {
      sesIoIn('render', 'isDisableSes_', 'expr=isDisableSes()');
      if (isDisableSes()) {
        sesIoOut('render', 'isDisableSes_', 'branch=true');
        return null;
      }
      sesIoOut('render', 'isDisableSes_', 'branch=false continue');

      sesIoIn(
        'render',
        'getEnrolmentBlockReason_this.props.user?.userProfile_',
        'expr=getEnrolmentBlockReason(this.props.user?.userProfile)'
      );
      if (getEnrolmentBlockReason(this.props.user?.userProfile)) {
        sesIoOut(
          'render',
          'getEnrolmentBlockReason_this.props.user?.userProfile_',
          'branch=true'
        );
        return <EnrolmentBlacklistStop />;
      }
      sesIoOut(
        'render',
        'getEnrolmentBlockReason_this.props.user?.userProfile_',
        'branch=false continue'
      );

      const course = this.resolvedCourse();
      sesIoIn(
        'render',
        '!course_||_Number.isNaN_this.courseId_',
        'expr=!course || Number.isNaN(this.courseId())'
      );
      if (!course || Number.isNaN(this.courseId())) {
        sesIoOut(
          'render',
          '!course_||_Number.isNaN_this.courseId_',
          'branch=true'
        );
        return <Redirect to="/learn/enrolment" />;
      }
      sesIoOut(
        'render',
        '!course_||_Number.isNaN_this.courseId_',
        'branch=false continue'
      );

      sesIoIn(
        'render',
        'isEnrolmentCourseFull_course_',
        'expr=isEnrolmentCourseFull(course)'
      );
      if (isEnrolmentCourseFull(course)) {
        sesIoOut('render', 'isEnrolmentCourseFull_course_', 'branch=true');
        const leave = () => this.props.history.push('/learn/enrolment');
        return (
          <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={ANIMATE.defaultPageVariants}
            className="page-container new-bking-container"
          >
            <h1 className="page-title">{I18n.t('ENROLMENT.TERMS.TITLE')}</h1>
            <Dialog open onClose={leave}>
              <DialogTitle>{I18n.t('ENROLMENT.UNABLE-TITLE')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {I18n.t('ENROLMENT.UNABLE-FULL-DESC')}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={leave} color="primary">
                  {I18n.t('COMMON.BACK')}
                </Button>
              </DialogActions>
            </Dialog>
          </motion.div>
        );
      }
      sesIoOut(
        'render',
        'isEnrolmentCourseFull_course_',
        'branch=false continue'
      );

      const paragraphs = this.policyParagraphs();
      const canAgree = this.state.scrolledToBottom;

      return (
        <motion.div
          initial="initial"
          animate="in"
          exit="out"
          variants={ANIMATE.defaultPageVariants}
          className="page-container new-bking-container"
        >
          <div className="page-title-row-with-action-btn">
            <h1 className="page-title">{I18n.t('ENROLMENT.TERMS.TITLE')}</h1>
            <Button onClick={() => this.onBack()}>
              {I18n.t('COMMON.BACK')}
            </Button>
          </div>

          <EnrolmentCourseHeader course={course} />

          <Grid
            container
            className="info-row add-player-row enrolment-terms-block"
            spacing={3}
          >
            <div className="bg-color right" />
            <Grid item xs={12}>
              <div className="enrolment-terms-stack">
                <div className="enrolment-terms-card">
                  <h2 className="enrolment-terms-policy-title">
                    {I18n.t('ENROLMENT.TERMS.POLICY-TITLE')}
                  </h2>
                  <div
                    className="enrolment-terms-policy-scroll"
                    style={{ maxHeight: 280, overflowY: 'auto' }}
                    ref={this.setPolicyRef}
                    onScroll={this.onPolicyScroll}
                  >
                    {this.state.policyHtml ? (
                      <div
                        className="enrolment-terms-policy-html"
                        dangerouslySetInnerHTML={{ __html: this.state.policyHtml }}
                      />
                    ) : this.state.policyReady ? (
                      paragraphs.map((text, idx) => (
                        <p key={idx} className="enrolment-terms-body">
                          {text}
                        </p>
                      ))
                    ) : null}
                  </div>
                  {!canAgree ? (
                    <FormHelperText className="enrolment-terms-scroll-hint">
                      {I18n.t('ENROLMENT.TERMS.SCROLL-HINT')}
                    </FormHelperText>
                  ) : null}
                  {this.state.showTermsError ? (
                    <FormHelperText error>
                      {I18n.t('ENROLMENT.TERMS.AGREE-REQUIRED')}
                    </FormHelperText>
                  ) : null}
                </div>
              </div>
            </Grid>

            <Grid
              container
              item
              xs={12}
              style={{ justifyContent: 'flex-end', marginTop: 20 }}
            >
              <Button
                style={{ maxHeight: '4rem' }}
                onClick={() => this.setState({ confirmCancel: true })}
              >
                {I18n.t('COMMON.CANCEL')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 15, maxHeight: '4rem' }}
                disabled={!canAgree}
                onClick={() => this.onAgree()}
              >
                {I18n.t('ENROLMENT.TERMS.ACCEPT')}
              </Button>
            </Grid>
          </Grid>

          <Dialog
            open={this.state.confirmCancel}
            onClose={() => this.setState({ confirmCancel: false })}
          >
            <DialogTitle>{I18n.t('ENROLMENT.ABORT-CONFIRM-TITLE')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {I18n.t('ENROLMENT.ABORT-CONFIRM-DESC')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ confirmCancel: false })}>
                {I18n.t('COMMON.BACK')}
              </Button>
              <Button
                onClick={() => this.onConfirmCancel()}
                color="primary"
                variant="contained"
              >
                {I18n.t('COMMON.CONFIRM')}
              </Button>
            </DialogActions>
          </Dialog>
        </motion.div>
      );
    } finally {
      sesFnEnd('render');
    }
  }
}

const mapStateToProps = (state: RootState) => {
  sesFnStart('mapStateToProps');
  try {
    return {
      i18n: state.i18n!,
      user: state.user!,
    };
  } finally {
    sesFnEnd('mapStateToProps');
  }
};

export default connect(mapStateToProps)(EnrolmentTerms);
